import { AddTagRequest, CreateLocationRequest, CreatePointRequest, CreatePointsResponse, CustomShape, GeoJsonGeometry, GeoJsonType, GeocodeType, TopicCategory, uploadImageUrlRequest } from '../types';
import { ZERO_OBJECT_ID } from '../helpers/constants';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, isDefined, smartTruncate } from '../helpers/utils';
import { getRandomColor, makeColorIntoDataSvg } from '../helpers/colors';

import { F } from '../helpers/togeojson/shared';
import { Loader } from '@googlemaps/js-api-loader';
import { PaperDialogElement } from '@polymer/paper-dialog';
import { PaperInputElement } from '@polymer/paper-input/paper-input';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import TurndownService from 'turndown';
import accountInfo from '../helpers/state/account-info';
import { addPoints } from '../helpers/api/points-api';
import { createErrorStringForBulk } from '../helpers/components/hot-points';
import { createTopicRequestFromTopic } from '../helpers/api/topics-api';
import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { kmlWithFolders } from '../helpers/togeojson/kml';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('kml-upload-component')
export class KmlUploadComponent extends LitElement {
    amplitude_callsite: string = 'KML Upload Component';



    @query('#inputFileSelect')
    inputFileSelect!: PaperInputElement;

    @query('#startedModal')
    startedModal!: PaperDialogElement;

    @query('#successModal')
    successModal!: PaperDialogElement;

    @query('#inputURL')
    inputURL!: TextField;

    @property({ type: Boolean })
    isWriter = false;

    @state()
    createPoints: CreatePointRequest[] = [];

    kmlObj!: Document;

    googleMapsLoader!: Loader;

    @property({ type: Boolean })
    bypassGoogleKey: boolean = false;

    @state()
    uploadValue: number = 0;

    @state()
    uploadLength: number = 1;

    @state()
    loaderProgress: number = 0;

    @state()
    createShapes: CustomShape[] = [];

    @state()
    uploaderMessage: string = 'Uploading...';


    // Eventually we should have the user select these
    // detailExcludeList = ['Zipcode', 'Zip Code', 'Address', 'Street Address'];
    detailIncludeList = ['Name', 'name', 'Description', 'description'];

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async updateTopicCategories(categoryName: string) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const new_category = await topicData.createNewCategory(this.amplitude_callsite);
            if (new_category._id) {
                await topicData.fetchAndUpdateCategory(new_category._id.$oid, {name: categoryName, icon: makeColorIntoDataSvg(getRandomColor())}, this.amplitude_callsite);
            }
        }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');
    }

    async closeSuccessModal() {
        this.uploadValue = 0;
        this.successModal.close();
        this.inputFileSelect.value = '';

        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
        }
    }

    async readKML() {
        const reader = new FileReader();
        logWithAmplitude(this.amplitude_callsite, 'KML Upload Attempt', {
            topic_id: topicData.topicId,
            topic_key: topicData.topicKey
        });

        // Validate the elements
        const inputFileSelectValid = this.inputFileSelect.validate();
        const valid = inputFileSelectValid;

        if (!valid) {
            getMainPage().errorToastElement.showToastAndThrow(
                'Please fix your invalid inputs (marked red).'
            );
        }

        // Paper Input doesn't handle type="File" gracefull
        // So we have to hack at it to access the base HTML
        const fileList = this.inputFileSelect.$.input.querySelector('input')?.files;

        if (typeof fileList === 'undefined') {
            getMainPage().errorToastElement.showToastAndThrow('No file selected. Please choose a KML file');
        }

        reader.addEventListener('load', (e) => {
            const inputBlob = e.target?.result;
            if (typeof inputBlob === 'undefined') {
                getMainPage().errorToastElement.showToastAndThrow(
                    'File is empty. Please choose a valid KML file.'
                );
            }

            // Now that KML is loaded, proceed with parsing
            try {
                this.startedModal.open();
                this.requestUpdate();
                this.uploaderMessage = 'Reading File...';
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.parseKML(inputBlob as string);
            } catch (e) {
                console.error(e);
                getMainPage().errorToastElement.showToastAndThrow(
                    'An error occurred while parsing the KML file. Please try again.'
                );
            }
        });

        reader.addEventListener('error', (e) => {
            alert('Failed to read file');
        });

        reader.readAsText((fileList as FileList)[0]);
    }

    async getKMLlink(url: string) {
        if (this.inputURL?.value?.length > 0 && !this.inputURL?.value.includes('mid=')) {
            getMainPage().errorToastElement.showToastAndThrow('Please enter a valid Google My Map link.');
        }

        const mapid = url.split('mid=')[1].split('&')[0];
        const download_url = `https://www.google.com/maps/d/u/0/kml?mid=${mapid}&forcekml=1`;
        // open in new tab
        window.open(download_url, '_blank');

    }

    async parseKML(inputBlob: string) {
        this.startedModal.open();
        // Set the upload category ID.
        let topicCategoryId = topicData.categories[0]?._id ?? { $oid: ZERO_OBJECT_ID };

        // Parse KML for points
        const xmlParser = new DOMParser();
        this.kmlObj = xmlParser.parseFromString(inputBlob, 'application/xml');

        const kmlData = kmlWithFolders(this.kmlObj);

        if (kmlData.children.length === 0) {
            this.startedModal.close();
            getMainPage().errorToastElement.showToastAndThrow('No places found in KML file.');
        }

        let numFolders = 0;
        let numPlacemarks = 0;
        let numShapes = 0;

        // Count the number of folders and placemarks
        for (const child of kmlData.children) {
            if (child.type === 'folder') {
                numFolders++;
                for (const folderChild of child.children) {
                    if (folderChild.type === 'Feature') {
                        if (folderChild.geometry?.type === 'Point') {
                            numPlacemarks++;
                        } else {
                            numShapes++;
                        }
                    }
                }
            } else if (child.type === 'Feature') {
                if (child.geometry?.type === 'Point') {
                    numPlacemarks++;
                } else {
                    numShapes++;
                }
            }
        }

        this.uploadLength = numPlacemarks + numShapes + numFolders;
        this.uploaderMessage = `Parsing ${numPlacemarks} points and ${numShapes} shapes...`;

        // iterate through each child of the kml object. If it is a folder, iterate through its children. If it is a feature with a type of "Placemark", add it to the places array
        for (const child of kmlData.children) {
            if (child.type === 'folder') {
                const foundCategory = topicData.categories.find((category) => category.name === child.meta.name as string);
                if (foundCategory?._id !== undefined) {
                    topicCategoryId = foundCategory._id;
                } else {
                    await this.updateTopicCategories(child.meta.name as string);
                    topicCategoryId = topicData.categories.find((category) => category.name === child.meta.name as string)?._id ?? topicCategoryId;
                }
                for (const folderChild of child.children) {
                    if (folderChild.type === 'Feature') {
                        await this.processFolderChild(folderChild, topicCategoryId.$oid);
                    }
                }
            } else if (child.type === 'Feature') {
                await this.processFolderChild(child, topicCategoryId.$oid);
            }
        }

        // get from the kml object all of the places

        if (this.createPoints.length === 0 && this.createShapes.length === 0) {
            this.startedModal.close();
            getMainPage().errorToastElement.showToastAndThrow('No points or shapes to upload. Please check your KML file.');
        }

        if (this.createPoints.length > 0) {
            this.uploaderMessage = 'Uploading points...';
            await this.submitPoints(this.createPoints);
        }

        if (this.createShapes.length > 0) {
            try {
                this.uploaderMessage = 'Uploading shapes...';
                await this.submitShapes();
            } catch (e) {
                this.startedModal.close();
                getMainPage().errorToastElement.showToastAndThrow(
                    'An error occurred while uploading shapes. Please try again.'
                );
            }
        }

        this.startedModal.close();
    }

    async processFolderChild(child: F, topicCategoryId: string) {
        if (child.geometry?.type === 'Point') {
            const point = await this.createPointFromGeoJsonFeature(child, topicCategoryId);
            if (point !== undefined) {
                this.createPoints = [...this.createPoints, point];
            }
        } else if (child.geometry?.type === 'LineString' || child.geometry?.type === 'Polygon' || child.geometry?.type === 'MultiPolygon' || child.geometry?.type === 'MultiLineString' || child.geometry?.type === 'GeometryCollection') {
            this.createShapeFromGeoJsonFeature(child);
        } else {
            // still attempt to make a point
            const point = await this.createPointFromGeoJsonFeature(child, topicCategoryId);
            if (point !== undefined) {
                this.createPoints = [...this.createPoints, point];
            }
        }
    }


    async submitPoints(newPoints: CreatePointRequest[]) {

        if (pointCache.allPoints.length >= featuresInfo.pointsPerMapEnabled) {
            this.startedModal.close();
            getMainPage().errorToastElement.showToastAndThrow(
                'You have reached the maximum number of points allowed on this map.  Please upgrade to add more.'
            );
            return;
        } else if (pointCache.allPoints.length + newPoints.length >= featuresInfo.pointsPerMapEnabled) {
            const pointsLeft = featuresInfo.pointsPerMapEnabled - pointCache.allPoints.length;
            this.startedModal.close();
            getMainPage().errorToastElement.showToastAndThrow(
                `You can only add ${pointsLeft} more points to this map. This upload contains ${newPoints.length}. Please upgrade to add more or reduce your file size.`
            );
            return;
        }

        // Submit all of the points.
        const chunkSize = 15;
        const tick = 2000;

        let results: CreatePointsResponse = {
            failed_geocodes: [],
            failed_validations: [],
            updated_point_ids: [],
        };

        for (let i = 0; i < newPoints.length; i += chunkSize) {
            const chunk = newPoints.slice(i, i + chunkSize);

            const result = await addPoints(
                this.amplitude_callsite,
                topicData.topicId,
                chunk,
                topicData.topicKey,
                GeocodeType.Place
            );

            results = {
                failed_geocodes: [...results.failed_geocodes, ...result.failed_geocodes],
                failed_validations: [...results.failed_validations, ...result.failed_validations],
                updated_point_ids: [...results.updated_point_ids, ...result.updated_point_ids],
            };

            await delay(tick);
            this.uploadValue += chunk.length;
            this.uploaderMessage = `Uploading points... ${this.uploadValue} of ${this.uploadLength}`;
        }

        this.successModal.open();
        this.startedModal.close();

        // Show any errors.
        const categoryIdMap: Map<string, TopicCategory> = new Map<string, TopicCategory>();
        for (const category of topicData.categories) {
            if (category._id === undefined) {
                continue;
            }
            categoryIdMap.set(category._id.$oid, category);
        }
        const errorString = createErrorStringForBulk([results], topicData.topic, categoryIdMap);

        if (errorString !== undefined) {
            getMainPage().downloadableErrorToastElement.showDownloadableToast(
                window.URL.createObjectURL(new Blob([errorString], { type: 'text/csv' })),
                'Errors occurred during KML Upload: ',
                results.failed_geocodes.length + results.failed_validations.length
            );
        }

        // Set a default timer to close out the success modal in the event of user inactivity
        // Time out occurs after one minute.
        window.setTimeout(async () => {
            await this.closeSuccessModal();
        }, 60 * 1000);
    }

    private async createPointFromGeoJsonFeature(place: F, topicCategoryId: string) {
        const nameLimit = 80;
        const descriptionLimit = 2999;

        const has_address = isDefined(place.properties?.address) || isDefined(place.properties?.city) || isDefined(place.properties?.state) || isDefined(place.properties?.zip);
        if (place.geometry?.type !== 'Point' && !has_address) {
            return;
        }

        let name = place.properties?.name ?? '';
        let description = '';
        let imageUrls: uploadImageUrlRequest[] = [];
        if (typeof place.properties?.description === 'object' && place.properties?.description !== null) {
            const returned_description = this.processHTMLDescription(place.properties.description.value);
            description = returned_description.description;
            imageUrls = returned_description.imageUrls;
        } else if (typeof place.properties?.description === 'string' || typeof place.properties?.description === 'number') {
            description = place.properties.description.toString();
        }

        // if there is a geometry, use it as the location. If not, use the address
        let locationRequest: CreateLocationRequest;
        if (place.geometry?.type !== 'Point') {
            const address = `${(place.properties?.address ?? '') as string}, ${(place.properties?.city ?? '') as string}, ${(place.properties?.state ?? '') as string}, ${(place.properties?.zip ?? '') as string}`;
            locationRequest = {
                latLng: undefined,
                search: address,
                place_id: undefined
            };
        } else {
            locationRequest = {
                latLng: {
                    lat: place.geometry.coordinates[1],
                    lng: place.geometry.coordinates[0],
                },
                search: undefined,
                place_id: 'custom',
            };
        }

        // Apply character length validation
        name = smartTruncate(name, nameLimit);
        description = smartTruncate(description, descriptionLimit);

        // iterate through

        const tag_info: AddTagRequest = {
            user_tags: []
        };

        const point: CreatePointRequest = {
            _id: undefined,
            email: undefined,
            topic_id: { $oid: topicData.topicId },
            topic_category_id: { $oid: topicCategoryId },
            name,
            location: locationRequest,
            description,
            special_offer: undefined,
            active: undefined,
            featured: undefined,
            start: undefined,
            end: undefined,
            source: undefined,
            embedded_video: undefined,
            custom_icon_url: undefined,
            tag_info,
            url_images: imageUrls,
            number: undefined,
            is_gate_hidden: undefined,
            custom_information: undefined,
            url: undefined,
            custom_shape: undefined, // This will be set later if the point is a shape
            // Assume that start and end will be undefined for the import
        };

        return point;

    };

    private createShapeFromGeoJsonFeature(place: F) {
        // tk remember to handle multi point
        if (!isDefined(place.geometry) || place.geometry?.type === 'Point' || place.geometry?.type === 'MultiPoint') {
            return;
        }

        let geometry_coordinates: [number, number]
                                    | Array<[number, number]>
                                    | Array<Array<[number, number]>>
                                    | Array<Array<Array<[number, number]>>>
                                    | GeoJsonGeometry[] = [];

        if (place.geometry?.type === 'LineString') {
            geometry_coordinates = place.geometry.coordinates.map((coordinate: any) => {
                return [coordinate[0], coordinate[1]];
            }
            );
        } else if (place.geometry?.type === 'Polygon') {
            geometry_coordinates = place.geometry.coordinates.map((coordinate: any) => {
                return coordinate.map((subCoordinate: any) => {
                    return [subCoordinate[0], subCoordinate[1]];
                });
            });
        } else if (place.geometry?.type === 'MultiLineString') {
            geometry_coordinates = place.geometry.coordinates.map((coordinate: any) => {
                return coordinate.map((subCoordinate: any) => {
                    return [subCoordinate[0], subCoordinate[1]];
                });
            });
        } else if (place.geometry?.type === 'MultiPolygon') {
            geometry_coordinates = place.geometry.coordinates.map((coordinate: any) => {
                return coordinate.map((subCoordinate: any) => {
                    return subCoordinate.map((subSubCoordinate: any) => {
                        return [subSubCoordinate[0], subSubCoordinate[1]];
                    });
                });
            });
        } else if (place.geometry?.type === 'GeometryCollection') {
            geometry_coordinates = place.geometry.geometries.map((geometry: any) => {
                let coordinates;
                if (geometry.type === 'LineString') {
                    coordinates = geometry.coordinates.map((coordinate: any) => {
                        return [coordinate[0], coordinate[1]];
                    });
                } else if (geometry.type === 'Polygon') {
                    coordinates = geometry.coordinates.map((coordinate: any) => {
                        return coordinate.map((subCoordinate: any) => {
                            return [subCoordinate[0], subCoordinate[1]];
                        });
                    });
                } else {
                    return geometry;
                }

                return {
                    type: geometry.type,
                    coordinates,
                };
            });
        }

        let description = '';
        if (typeof place.properties?.description === 'object' && place.properties?.description !== null) {
            description = this.processHTMLDescription(place.properties.description.value, true).description;
        } else if (typeof place.properties?.description === 'string' || typeof place.properties?.description === 'number') {
            description = place.properties.description.toString();
        }

        const shape: CustomShape = {
            name: place.properties?.name ?? '',
            description,
            geojson: {
                type: place.geometry.type as GeoJsonType,
                coordinates: geometry_coordinates,
            },
            style: {
                stroke_color: place.properties?.stroke ?? '#000000',
                stroke_opacity: place.properties?.strokeOpacity ?? 1,
                stroke_weight: place.properties?.strokeWeight ?? 1,
                fill_color: place.properties?.fill ?? '#000000',
                fill_opacity: place.properties?.fillOpacity ?? 1,
            },
        };

        this.createShapes = [...this.createShapes, shape];
    }

    private processHTMLDescription(description: string, text_only: boolean = false) {
        const imageUrls: uploadImageUrlRequest[] = [];
        // process images
        if (!text_only) {
            const imgRegex = /<img[^>]+src="([^">]+)"[^>]*>/g;
            let match;
            while ((match = imgRegex.exec(description)) !== null) {
                try {
                    const parser = new DOMParser();
                    const htmlDoc = parser.parseFromString(match[0], 'text/html');
                    const imgElement = htmlDoc.querySelector('img');
                    if (imgElement) {
                        const src = imgElement.getAttribute('src');
                        if (src !== null) {
                            imageUrls.push({
                                url: src,
                                name:
                                    imgElement.getAttribute('alt') ??
                                    imgElement.getAttribute('title') ??
                                    'kml image upload'
                            });
                        }
                    }
                } catch (error) {
                    getMainPage().errorToastElement.showToast(`Error parsing an image from KML file for upload`);
                }
            }

            // remove the image elements from the description
            description = description.replace(/<img[^>]+>/g, '');
        }
        // turn to markdown
        const turndownService = new TurndownService();
        description = turndownService.turndown(description);
        return { description, imageUrls };
    }


    private async submitShapes() {
        const createTopicRequest = createTopicRequestFromTopic(topicData.topic);
        createTopicRequest.custom_shapes = [...(createTopicRequest.custom_shapes ?? []), ...this.createShapes];
        await topicData.fetchAndUpdateTopicData(createTopicRequest, this.amplitude_callsite);
    }


    render() {
        return html`
            <div id="container" class="container">
                <p>
                    This feature allows map admins to transfer points from Google My Maps and KML format to Proxi.Proxi will parse your KML file, extract places, descriptions, and shapes, and add them to your map.
                    <br><br>
                    If your points have assigned categories, we will check if these categories exist on your map. If they do, we will add the points accordingly. If they don’t, we will create the categories and add the points. If there are no categories, we will add the points to the first category.
                </p>


            <h3>Already have your KML File? Upload it here.</h3>
            <paper-input id="inputFileSelect" accept=".kml" type="file" label="Select KML file" required></paper-input>
            <mwc-button  class="save button" @click="${this.readKML}" raised>Upload</mwc-button>
            <hr />

            <h3>Need To Get Your File From Google My Maps? Input your map link here.</h3>
            <p><b>Ensure that your map is viewable by anyone with the link</b> and paste the link here. The link should have the following format:
                <br><br>
                <i> https://www.google.com/maps/d/edit?mid=1oYRW-g71Zhne2ezXLQAbqQ1v3PLmmEs...</i>
            </p>
            <mwc-textfield id="inputURL" label="Your Google My Map Link" outlined style="width:100%"></mwc-textfield>
            <br />
            ${this.inputURL?.value?.length > 0 && !this.inputURL?.value.includes('mid=') ? html`
                <p style="color: red;">Please enter a valid Google My Map link. Refer to the example link</p>
                <br />
                <br />
            ` : ''}

            <mwc-button class="save button" @click="${async () => await this.getKMLlink(this.inputURL.value)}" raised>Download KML File for My Map</mwc-button>

            <error-toast id="errorToast"></error-toast>

            <paper-dialog id="successModal">
                <paper-item>
                    <p>Upload completed!
                        <br>
                        We added ${this.uploadValue} points and ${this.createShapes.length} shapes.
                        <br/>
                        Your ${topicData.topic?.point_name ?? 'point'}s and shapes will show up on the map in about one minute.
                        <br /> You may need to refresh the page to see them all appear.
                    </p>
                    <paper-button @click="${this.closeSuccessModal}" raised>Dismiss</paper-button>
                </paper-item>
            </paper-dialog>

            <paper-dialog id="startedModal">
                <paper-item>
                    <p>Upload in progress, please wait for the success message and remain on this page.</p>
                    <br>
                    <br>
                    <p>${this.uploaderMessage}</p>
                </paper-item>
            </paper-dialog>
            </div>
        `;
    }

    static get styles() {
        return css`
            .container {
                display: block;
                padding-right: 20px;
                overflow: auto;
            }

            .paper-item {
                display: block;
                text-align: center;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }
        `;
    }
}
